<template>
	<div
		class="grid items-start grid-cols-1 md:grid-cols-3 gap-x-[40px] max-w-[345px] sm:max-w-[680px] md:max-w-[1420px] lg:min-w-[900px] mx-auto"
	>
		<BaseCloudImage
			v-if="showCompany && jobData?.company?.logo?.cloudinary"
			:class="'-mt-10'"
			class="md:hidden w-dj-80"
			custom-class="rounded-normal overflow-hidden z-0"
			:public-id="jobData.company.logo.cloudinary"
			postfix=".jpg"
			alt="company logo"
			:transformation="[{ width: 80, height: 80, crop: 'scale' }]"
		/>
		<div class="md:col-span-2 flex flex-col">
			<JobProfileInfo
				class="mt-4 md:mt-9 mb-4 md:mb-7"
				:name="jobData.name[locale] || ''"
				:job-data="jobData"
				:company="jobData?.company?.name"
				:company-slug="jobData?.company?.slug"
				:tags="jobData?.jobTags || []"
				:salary="jobData?.jobSalary"
				:location="jobData?.job_offices?.[0]?.formatted_address ?? ''"
				:jobOffices="jobData?.tags?.office_location || []"
				:jobBadges="jobData.badges || []"
			/>
			<DjBoxRte
				class="dj-box-job-about hidden md:block"
				:title="$t('job.about')"
				:copy="jobData?.boxes?.job_job_description?.data?.description?.[locale] ?? ''"
				without-margin
			></DjBoxRte>
			<BaseLink class="self-start text-sm md:hidden" @click="scrollToJobDetails" type="blue">
				{{ $t("job.details") }}
				<BaseIcon class="ml-2" icon="akar-icons:chevron-down" color="currentColor" />
			</BaseLink>
		</div>

		<div class="flex flex-col w-full mt-4 md:mt-9" :class="showCompany ? 'justify-start' : 'md:pt-8'">
			<JobProfileCard
				class="md:sticky md:top-8"
				:job-data="jobData"
				:logo="jobData?.company?.logo?.cloudinary || ''"
				:job-name="jobData.name[locale] || ''"
				:job-slug="jobSlug.value"
				:location="jobData?.job_offices?.[0]?.formatted_address ?? ''"
				:job-tags="jobData?.tags || []"
				:expires-date="expiresDate"
				:company-slug="jobData?.company?.slug || ''"
				:company-name="jobData?.company?.name || ''"
				:already-applied="alreadyApplied"
				:saved-job="savedJob"
				:company-id="jobData?.company?.id || 0"
				@apply="$emit('apply')"
				@on-saved-change="$emit('onSavedChange', $event)"
			/>
		</div>
		<div id="job-details"></div>
	</div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import { type JobItem, getJobSlug } from "@@/composables/useJob";
import { isApplicationDeadlineUnlimited } from "@@/utils/jobDeadline";

export default defineComponent({
	name: "JobProfileHeader",
	props: {
		alreadyApplied: {
			type: Boolean,
			required: true,
		},
		savedJob: {
			type: Boolean,
			default: false,
		},
		showCompany: {
			type: Boolean,
			default: true,
		},
		jobData: {
			type: Object as PropType<JobItem>,
			default: null,
		},
	},
	emits: ["apply", "onSavedChange"],
	setup(props) {
		const isShowCompany = ref<boolean>(true);
		const { locale } = useI18n();
		const expiresDate = ref<string>("");

		isShowCompany.value = props.jobData.company_id !== null;

		if (isApplicationDeadlineUnlimited(props.jobData)) {
			expiresDate.value = "unlimited";
		} else {
			expiresDate.value = props.jobData.expires_at ? props.jobData.expires_at : "expired";
		}

		const jobSlug = ref(getJobSlug(props.jobData, locale.value));

		function scrollToJobDetails() {
			const element = document.getElementById("job-details");

			if (!element) return;

			window.scrollTo({ top: element.offsetTop, left: 0, behavior: "smooth" });
		}

		return {
			scrollToJobDetails,
			locale,
			expiresDate,
			isShowCompany,
			jobSlug,
		};
	},
});
</script>
